import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FiShoppingBag, 
  FiUser, 
  FiCalendar, 
  FiDollarSign, 
  FiPackage, 
  FiClock, 
  FiAlertCircle,
  FiCheckCircle,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiTrendingUp,
  FiCreditCard
} from "react-icons/fi";

import OrderFormPanel from "../components/orders/OrderFormPanel";
import SectionHeader from "../components/ui/SectionHeader";
import StatusBadge from "../components/ui/StatusBadge";
import {
  useDeleteOrderMutation,
  useGetCustomersQuery,
  useGetEmployeesQuery,
  useGetOrdersQuery,
  useUpdateOrderMutation,
} from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";
import { money, shortDate } from "../utils/formatters";

const EMPTY_FORM = {
  customer_id: "",
  title: "",
  product_type: "",
  description: "",
  quantity: "",
  unit_price: "",
  priority: "medium",
  deadline: "",
  assigned_employee_id: "",
  order_status: "pending",
  payment_mode: "none",
  paid_amount: 0,
  payment_method: "cash",
};

const PRIORITY_COLOR = {
  urgent: "#ef4444",
  high:   "#f59e0b",
  medium: "#3b82f6",
  low:    "#6b7280",
};

const PRIORITY_BG = {
  urgent: "#fef2f2",
  high:   "#fef3c7",
  medium: "#eff6ff",
  low:    "#f9fafb",
};

const STATUS_COLORS = {
  pending: { bg: "#fef3c7", color: "#f59e0b" },
  design_review: { bg: "#dbeafe", color: "#3b82f6" },
  approved: { bg: "#e0e7ff", color: "#6366f1" },
  in_production: { bg: "#ddd6fe", color: "#8b5cf6" },
  quality_check: { bg: "#fce7f3", color: "#ec4899" },
  ready: { bg: "#d1fae5", color: "#10b981" },
  delivered: { bg: "#d1fae5", color: "#059669" },
  cancelled: { bg: "#fee2e2", color: "#ef4444" },
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const [form, setForm]           = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { data, isLoading }       = useGetOrdersQuery();
  const { data: customers }       = useGetCustomersQuery();
  const { data: employees }       = useGetEmployeesQuery();
  const [updateOrder]             = useUpdateOrderMutation();
  const [deleteOrder]             = useDeleteOrderMutation();

  const orders = data?.data || [];

  const filtered = orders.filter((o) => {
    const matchSearch = !search ||
      o.title.toLowerCase().includes(search.toLowerCase()) ||
      (o.customer_name || "").toLowerCase().includes(search.toLowerCase()) ||
      o.product_type.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || o.order_status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleEdit = (order) => {
    setEditingId(order.id);
    setForm({
      customer_id:          order.customer_id,
      title:                order.title,
      product_type:         order.product_type,
      description:          order.description || "",
      quantity:             order.quantity ?? "",
      unit_price:           order.unit_price ?? "",
      priority:             order.priority || "medium",
      deadline:             order.deadline || "",
      assigned_employee_id: order.assigned_employee_id || "",
      order_status:         order.order_status || "pending",
      payment_mode:         "none",
      paid_amount:          0,
      payment_method:       "cash",
    });
    // Scroll to form
    setTimeout(() => {
      document.getElementById("order-edit-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    try {
      await updateOrder({ id: editingId, ...form }).unwrap();
      setEditingId(null);
      setForm(EMPTY_FORM);
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to update order."));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await deleteOrder(id).unwrap();
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, "Unable to delete order."));
    }
  };

  // Summary stats
  const totalRevenue  = orders.reduce((s, o) => s + Number(o.total_price), 0);
  const totalPaid     = orders.reduce((s, o) => s + Number(o.paid_amount), 0);
  const totalBalance  = orders.reduce((s, o) => s + Number(o.remaining_balance), 0);
  const overdueCount  = orders.filter((o) => o.is_overdue).length;

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Order Management"
        title="Track production runs, deadlines, and payments"
        action={
          <button
            className="primary-button"
            type="button"
            onClick={() => navigate("/orders/create")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "0.75rem 1.5rem",
              background: "linear-gradient(135deg, #0F5D4F 0%, #22A06B 100%)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "0.9375rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            <FiShoppingBag size={18} />
            New Order
          </button>
        }
      />

      {/* Summary Stats Cards */}
      <div className="stats-grid-modern" style={{ marginBottom: "2rem" }}>
        <div className="stat-card-modern stat-card-modern--primary">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Total Revenue</span>
            <div className="stat-card-modern__icon" style={{ background: "rgba(255,255,255,0.2)" }}>
              <FiTrendingUp style={{ color: "#fff" }} />
            </div>
          </div>
          <div className="stat-card-modern__value">{money(totalRevenue)}</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta" style={{ color: "rgba(255,255,255,0.85)" }}>
              From {orders.length} orders
            </span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Collected</span>
            <div className="stat-card-modern__icon" style={{ background: "#d1fae5", color: "#10b981" }}>
              <FiCheckCircle />
            </div>
          </div>
          <div className="stat-card-modern__value" style={{ color: "#10b981" }}>{money(totalPaid)}</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta">Payments received</span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Outstanding</span>
            <div className="stat-card-modern__icon" style={{ background: "#fee2e2", color: "#ef4444" }}>
              <FiCreditCard />
            </div>
          </div>
          <div className="stat-card-modern__value" style={{ color: totalBalance > 0 ? "#ef4444" : "#10b981" }}>
            {money(totalBalance)}
          </div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta">Pending payments</span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Overdue Orders</span>
            <div className="stat-card-modern__icon" style={{ background: overdueCount > 0 ? "#fee2e2" : "#d1fae5", color: overdueCount > 0 ? "#ef4444" : "#10b981" }}>
              <FiAlertCircle />
            </div>
          </div>
          <div className="stat-card-modern__value" style={{ color: overdueCount > 0 ? "#ef4444" : "#10b981" }}>
            {overdueCount}
          </div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta">Deadline alerts</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        display: "flex", 
        gap: "1rem", 
        marginBottom: "1.5rem",
        flexWrap: "wrap",
        background: "var(--bg-surface)",
        padding: "1rem",
        borderRadius: "12px",
        border: "1px solid var(--border)"
      }}>
        <div className="form-input-group" style={{ flex: "1 1 300px", minWidth: "200px" }}>
          <span className="form-input-group__icon"><FiSearch /></span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search orders, customers, products..."
            className="form-input"
            style={{ padding: "0.75rem 0.75rem 0.75rem 2.75rem" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: "0 0 auto" }}>
          <FiFilter size={18} style={{ color: "var(--text-muted)" }} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              border: "1px solid var(--border)",
              background: "var(--bg-base)",
              color: "var(--text-primary)",
              fontSize: "0.9375rem",
              cursor: "pointer",
              minWidth: "180px"
            }}
          >
            <option value="">All Statuses</option>
            {["pending","design_review","approved","in_production","quality_check","ready","delivered","cancelled"].map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="gallery-admin-grid">
        {isLoading && (
          <div className="empty-state-modern" style={{ gridColumn: "1 / -1" }}>
            <div className="loading-spinner"></div>
            <p>Loading orders...</p>
          </div>
        )}
        
        {!isLoading && filtered.length === 0 && (
          <div className="empty-state-modern" style={{ gridColumn: "1 / -1" }}>
            <FiShoppingBag />
            <p>{search || statusFilter ? "No orders found matching your filters" : "No orders yet"}</p>
          </div>
        )}

        {filtered.map((order) => (
          <div key={order.id} className="gallery-admin-card" style={{ 
            borderLeft: order.is_overdue ? "4px solid #ef4444" : "4px solid transparent",
            background: order.is_overdue ? "#fef2f2" : "var(--bg-base)"
          }}>
            {/* Order Header */}
            <div style={{ 
              padding: "1rem 1rem 0.75rem",
              borderBottom: "1px solid var(--border)",
              background: STATUS_COLORS[order.order_status]?.bg || "#f9fafb"
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                    <span style={{ 
                      fontSize: "0.75rem", 
                      color: "var(--text-muted)",
                      fontWeight: "600"
                    }}>
                      ORDER #{order.id}
                    </span>
                    {order.is_overdue && (
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "2px 8px",
                        background: "#fee2e2",
                        color: "#ef4444",
                        borderRadius: "12px",
                        fontSize: "0.7rem",
                        fontWeight: "600"
                      }}>
                        <FiAlertCircle size={10} />
                        OVERDUE
                      </span>
                    )}
                  </div>
                  <h4 style={{ 
                    margin: "0", 
                    fontSize: "1rem", 
                    fontWeight: "700",
                    color: "var(--text-primary)",
                    lineHeight: "1.3"
                  }}>
                    {order.title}
                  </h4>
                </div>
                
                {/* Priority Badge */}
                <div style={{
                  padding: "4px 10px",
                  borderRadius: "12px",
                  background: PRIORITY_BG[order.priority],
                  color: PRIORITY_COLOR[order.priority],
                  fontSize: "0.7rem",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  flexShrink: 0
                }}>
                  {order.priority}
                </div>
              </div>

              {/* Product Type */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                <FiPackage size={14} />
                <span>{order.product_type}</span>
              </div>
            </div>

            {/* Order Details */}
            <div className="gallery-admin-card__content">
              {/* Customer */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "0.5rem", fontSize: "0.875rem" }}>
                <FiUser size={14} style={{ color: "var(--text-muted)" }} />
                <span style={{ fontWeight: "600", color: "var(--text-primary)" }}>
                  {order.customer_name || `Customer #${order.customer_id}`}
                </span>
              </div>

              {/* Deadline */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "0.75rem", fontSize: "0.8125rem" }}>
                <FiCalendar size={14} style={{ color: order.is_overdue ? "#ef4444" : "var(--text-muted)" }} />
                <span style={{ color: order.is_overdue ? "#ef4444" : "var(--text-muted)", fontWeight: order.is_overdue ? "600" : "normal" }}>
                  Deadline: {shortDate(order.deadline)}
                </span>
              </div>

              {/* Status Badges */}
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                <div style={{
                  padding: "6px 12px",
                  borderRadius: "20px",
                  background: STATUS_COLORS[order.order_status]?.bg || "#f9fafb",
                  color: STATUS_COLORS[order.order_status]?.color || "#6b7280",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  textTransform: "capitalize"
                }}>
                  {order.order_status.replace(/_/g, " ")}
                </div>
                <div style={{
                  padding: "6px 12px",
                  borderRadius: "20px",
                  background: order.payment_status === "paid" ? "#d1fae5" : order.payment_status === "partial" ? "#fef3c7" : "#fee2e2",
                  color: order.payment_status === "paid" ? "#059669" : order.payment_status === "partial" ? "#f59e0b" : "#ef4444",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  textTransform: "capitalize"
                }}>
                  {order.payment_status}
                </div>
              </div>

              {/* Financial Summary */}
              <div style={{
                padding: "0.75rem",
                background: "var(--bg-subtle)",
                borderRadius: "8px",
                marginBottom: "0.75rem"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>Quantity:</span>
                  <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>{Number(order.quantity).toLocaleString()}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>Unit Price:</span>
                  <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>{money(order.unit_price)}</span>
                </div>
                <div style={{ height: "1px", background: "var(--border)", margin: "0.5rem 0" }}></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "var(--text-primary)" }}>Total:</span>
                  <span style={{ fontSize: "1rem", fontWeight: "800", color: "#0F5D4F" }}>{money(order.total_price)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                  <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>Paid:</span>
                  <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#10b981" }}>{money(order.paid_amount)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>Balance:</span>
                  <span style={{ 
                    fontSize: "0.875rem", 
                    fontWeight: "700",
                    color: Number(order.remaining_balance) > 0 ? "#ef4444" : "#10b981"
                  }}>
                    {money(order.remaining_balance)}
                  </span>
                </div>
              </div>

              {/* Assigned Employee */}
              {order.assigned_employee_name && (
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px", 
                  padding: "0.5rem",
                  background: "#eff6ff",
                  borderRadius: "6px",
                  marginBottom: "0.75rem",
                  fontSize: "0.8125rem"
                }}>
                  <FiUser size={14} style={{ color: "#3b82f6" }} />
                  <span style={{ color: "#1e40af", fontWeight: "500" }}>
                    Assigned to: {order.assigned_employee_name}
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="gallery-admin-card__actions">
                <button 
                  className="btn-icon" 
                  type="button" 
                  onClick={() => navigate(`/orders/${order.id}`)}
                  style={{ borderColor: "#3b82f6", color: "#3b82f6" }}
                >
                  <FiEye size={16} />
                  View
                </button>
                <button 
                  className="btn-icon btn-icon--edit" 
                  type="button" 
                  onClick={() => handleEdit(order)}
                >
                  <FiEdit2 size={16} />
                  Edit
                </button>
                <button 
                  className="btn-icon btn-icon--delete" 
                  type="button" 
                  onClick={() => handleDelete(order.id)}
                >
                  <FiTrash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Form */}
      {editingId && (
        <div id="order-edit-form" style={{ marginTop: "2rem" }}>
          <OrderFormPanel
            editingId={editingId}
            form={form}
            customers={customers}
            employees={employees}
            submitError={submitError}
            onSubmit={handleSubmit}
            onChange={setForm}
            onCancel={() => {
              setEditingId(null);
              setForm(EMPTY_FORM);
              setSubmitError("");
            }}
          />
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
